import { useCallback, useMemo } from 'react';
// logic
import { useFeasibility } from 'hooks/useFeasibility';
import useDownloadControlDocument, {
  isControlDocumentAvailable,
} from 'hooks/useDownloadControlDocument';
import copy from 'copy-to-clipboard';
import routes from 'utils/routes';
import { push } from 'connected-react-router';
// state
import { useDispatch } from 'react-redux';
import { actions as orderActions } from 'state/order';
import { actions as uiActions } from 'state/ui';
import { actions as ordersActions } from 'state/orders';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import {
  getIsCancellable,
  getIsEditable,
  getIsRepeatable,
} from 'utils/order/methods';

const useActionsMenu = ({
  handleOptionClick,
  cargoManifests,
  numberOfStops,
  order,
  onUrlCopied,
  type,
}) => {
  const dispatch = useDispatch();

  const { getIsOrderNonCore } = useFeasibility();
  const isOrderNonCore = useMemo(
    () => getIsOrderNonCore(order),
    [getIsOrderNonCore, order]
  );
  //   tracking
  const { id: orderId, shared_token: sharedToken } = order;

  //   send data event
  const sendEvent = useCallback(
    (data) => dispatch(analyticsActions.sendEvent(data)),
    [dispatch]
  );

  //   cancel
  const isCancellable = getIsCancellable(order);

  const toggleCancelOrderModalOpened = useCallback(
    (data) => dispatch(uiActions.toggleCancelOrderModalOpened(data)),
    [dispatch]
  );

  const cancelOrder = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      sendEvent(
        analyticsEvents.shipperOrderCancelClicked({
          place: 'CARD',
          orderId: order.id,
        })
      );
      toggleCancelOrderModalOpened({ order });
    },
    [handleOptionClick, sendEvent, order, toggleCancelOrderModalOpened]
  );

  //   repeat
  const isRepeatable = !isOrderNonCore && getIsRepeatable(order);
  const repeatOrder = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      dispatch(
        orderActions.repeatOrder({ originalOrder: order, place: 'CARD' })
      );
    },
    [handleOptionClick, order, dispatch]
  );

  //   edit
  const isEditable = !isOrderNonCore && getIsEditable(order);
  const editOrder = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      sendEvent(
        analyticsEvents.shipperOrderEditClicked({
          place: 'CARD',
          orderId: order.id,
          status: order.status,
        })
      );
      dispatch(orderActions.editOrder({ originalOrder: order }));
    },
    [handleOptionClick, order, dispatch, sendEvent]
  );

  //   download manifest
  const isDownloadable = cargoManifests.length > 0;

  const downloadManifest = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const shouldOpenOrder = numberOfStops > 2 && cargoManifests.length > 1;
      if (shouldOpenOrder) {
        dispatch(ordersActions.openOrder({ orderId: orderId, type: type }));
      } else {
        window.open(cargoManifests[0], '_blank');
      }
    },
    [dispatch, orderId, type, numberOfStops, cargoManifests]
  );

  //   download DeCA
  const isControlDocumentDownloadable = isControlDocumentAvailable(order);
  const { downloadControlDocument: downloadOrderControlDocument } =
    useDownloadControlDocument();

  const downloadControlDocument = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      downloadOrderControlDocument(orderId);
    },
    [handleOptionClick, downloadOrderControlDocument, orderId]
  );

  const showShareActions = sharedToken;

  const trackingUrl = useMemo(
    () =>
      `${window.location.origin}${routes.tracking}?so=${orderId}&lt=${sharedToken}`,
    [orderId, sharedToken]
  );

  const openTrackingPage = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      sendEvent(
        analyticsEvents.shipperOrderTrackingPageRedirect({
          place: 'CARD',
          orderId: order.id,
        })
      );
      window.open(trackingUrl);
    },
    [handleOptionClick, order, sendEvent, trackingUrl]
  );

  const copyTrackingPageLink = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick();
      onUrlCopied();
      copy(trackingUrl);
      sendEvent(
        analyticsEvents.shipperOrderCopyTrackingPageLink({
          place: 'CARD',
          orderId: order.id,
        })
      );
    },
    [handleOptionClick, order, sendEvent, trackingUrl, onUrlCopied]
  );

  //   print
  const printOrderDetails = useCallback(() => {
    const printingURL = `${routes.orders[type]}/${orderId}?print=true`;
    dispatch(push(printingURL));
  }, [dispatch, type, orderId]);

  return useMemo(
    () => ({
      isCancellable,
      cancelOrder,
      isRepeatable,
      repeatOrder,
      isEditable,
      editOrder,
      isDownloadable,
      downloadManifest,
      showShareActions,
      openTrackingPage,
      copyTrackingPageLink,
      printOrderDetails,
      isControlDocumentDownloadable,
      downloadControlDocument,
    }),
    [
      isCancellable,
      cancelOrder,
      isRepeatable,
      repeatOrder,
      isEditable,
      editOrder,
      isDownloadable,
      downloadManifest,
      showShareActions,
      openTrackingPage,
      copyTrackingPageLink,
      printOrderDetails,
      isControlDocumentDownloadable,
      downloadControlDocument,
    ]
  );
};

export default useActionsMenu;
